import { apiClient } from "./client";
import type {
  Simulation,
  SimulationWithDetails,
  SimulationsPaginated,
  SimulationAdminPaginated,
  SimulationExecuteRequest,
  SimulationExecuteResponse,
  SimulationStatus,
  SimulationStats,
  SimulationDraft,
  SimulationDraftCreate,
  VroomImportAvailability,
  VroomImportResult,
  SimulationEventAdminPaginated,
  SimulationEventUser,
  SimulationEventUserPaginated,
  MessageResponse,
} from "@/types";

// Execute simulation
export async function executeSimulation(
  versionId: number,
  data: SimulationExecuteRequest
): Promise<SimulationExecuteResponse> {
  const response = await apiClient.post<SimulationExecuteResponse>(
    `/simulators/versions/${versionId}/simulate`,
    data
  );
  return response.data;
}

// List simulations for a simulator
interface ListSimulationsParams {
  page?: number;
  page_size?: number;
  version_id?: number;
  user_id?: number;
  status?: SimulationStatus;
}

export async function listSimulations(
  simulatorId: number,
  params: ListSimulationsParams = {}
): Promise<SimulationsPaginated> {
  const response = await apiClient.get<SimulationsPaginated>(
    `/simulators/${simulatorId}/simulations`,
    { params }
  );
  return response.data;
}

// Get simulation by ID
export async function getSimulation(
  simulationId: number
): Promise<SimulationWithDetails> {
  const response = await apiClient.get<SimulationWithDetails>(
    `/simulators/simulations/${simulationId}`
  );
  return response.data;
}

// List my simulations
export type SimulationOwnershipMode = "owned" | "shared";

interface ListMySimulationsParams {
  page?: number;
  page_size?: number;
  archived?: boolean;
  search?: string;
  folder_id?: number;
  simulator_id?: number;
  agency_id?: number;
  date_from?: string;
  date_to?: string;
  mode?: SimulationOwnershipMode;
}

export async function listMySimulations(
  pageOrParams: number | ListMySimulationsParams = 1,
  pageSize = 20,
  archived = false,
  search?: string,
  folderId?: number
): Promise<SimulationsPaginated> {
  let params: Record<string, unknown>;

  if (typeof pageOrParams === "object") {
    params = { ...pageOrParams };
  } else {
    params = { page: pageOrParams, page_size: pageSize, archived };
    if (search) params.search = search;
    if (folderId !== undefined) params.folder_id = folderId;
  }

  const response = await apiClient.get<SimulationsPaginated>(
    "/simulators/simulations/my",
    { params }
  );
  return response.data;
}

// List all simulations (admin only)
interface ListAllSimulationsParams {
  page?: number;
  page_size?: number;
  simulator_id?: number;
  user_id?: number;
  status?: SimulationStatus;
  archived?: boolean;
  date_from?: string;
  date_to?: string;
  pricing_policy_status?: string;
  shared_only?: boolean;
}

export async function listAllSimulations(
  params: ListAllSimulationsParams = {}
): Promise<SimulationAdminPaginated> {
  const response = await apiClient.get<SimulationAdminPaginated>(
    "/simulators/admin/simulations",
    { params }
  );
  return response.data;
}

// Export all simulations to Excel (admin only)
interface ExportSimulationsParams {
  simulator_id?: number;
  user_id?: number;
  status?: SimulationStatus;
  date_from?: string;
  date_to?: string;
  pricing_policy_status?: string;
  shared_only?: boolean;
}

export async function exportSimulationsExcel(
  params: ExportSimulationsParams = {}
): Promise<void> {
  const response = await apiClient.get("/simulators/admin/simulations/export", {
    params,
    responseType: "blob",
  });

  const contentDisposition = response.headers["content-disposition"];
  let filename = "cotations.xlsx";
  if (contentDisposition) {
    const match = contentDisposition.match(/filename=(.+)/);
    if (match) filename = match[1];
  }

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Export my simulations as JSON
export async function exportMySimulationsJson(): Promise<void> {
  const response = await apiClient.get("/simulators/simulations/my/export/json", {
    responseType: "blob",
  });

  const contentDisposition = response.headers["content-disposition"];
  let filename = "cotations.json";
  if (contentDisposition) {
    const match = contentDisposition.match(/filename=(.+)/);
    if (match) filename = match[1];
  }

  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// Delete simulation (only archived simulations can be deleted)
export async function deleteSimulation(
  simulationId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/simulations/${simulationId}`
  );
  return response.data;
}

// Archive simulation
export async function archiveSimulation(
  simulationId: number
): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(
    `/simulators/simulations/${simulationId}/archive`
  );
  return response.data;
}

// Unarchive (restore) simulation
export async function unarchiveSimulation(
  simulationId: number
): Promise<MessageResponse> {
  const response = await apiClient.post<MessageResponse>(
    `/simulators/simulations/${simulationId}/unarchive`
  );
  return response.data;
}

// Update simulation folder
export async function updateSimulationFolder(
  simulationId: number,
  folderId: number | null
): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>(
    `/simulators/simulations/${simulationId}/folder`,
    null,
    { params: { folder_id: folderId } }
  );
  return response.data;
}

// Share / unshare a simulation with another user (1:1, modifiable).
// Pass `sharedWithUserId = null` to remove an existing share.
export async function shareSimulation(
  simulationId: number,
  sharedWithUserId: number | null
): Promise<MessageResponse> {
  const response = await apiClient.patch<MessageResponse>(
    `/simulators/simulations/${simulationId}/share`,
    { shared_with_user_id: sharedWithUserId }
  );
  return response.data;
}

// Get simulation stats
export async function getSimulationStats(
  simulatorId: number,
  days = 30
): Promise<SimulationStats> {
  const response = await apiClient.get<SimulationStats>(
    `/simulators/${simulatorId}/simulations/stats`,
    { params: { days } }
  );
  return response.data;
}

// ============== Draft API ==============

// Get draft for a version (returns null if no draft exists)
export async function getDraft(
  versionId: number
): Promise<SimulationDraft | null> {
  try {
    const response = await apiClient.get<SimulationDraft | null>(
      `/simulators/versions/${versionId}/draft`
    );
    return response.data;
  } catch (error: unknown) {
    // If 404, return null (no draft exists)
    if (error && typeof error === "object" && "response" in error) {
      const axiosError = error as { response?: { status?: number } };
      if (axiosError.response?.status === 404) {
        return null;
      }
    }
    throw error;
  }
}

// Save draft (create or update)
export async function saveDraft(
  versionId: number,
  data: SimulationDraftCreate
): Promise<SimulationDraft> {
  const response = await apiClient.put<SimulationDraft>(
    `/simulators/versions/${versionId}/draft`,
    data
  );
  return response.data;
}

// Delete draft
export async function deleteDraft(
  versionId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/versions/${versionId}/draft`
  );
  return response.data;
}

// ============== Import VROOM (OPTIMISS) ==============

// Whether the version accepts a VROOM file, and up to how many circuits
export async function getVroomImportAvailability(
  versionId: number
): Promise<VroomImportAvailability> {
  const response = await apiClient.get<VroomImportAvailability>(
    `/simulators/versions/${versionId}/import-vroom`
  );
  return response.data;
}

// Read a VROOM file into form values; nothing is saved server-side
export async function importVroomFile(
  versionId: number,
  file: File
): Promise<VroomImportResult> {
  const formData = new FormData();
  formData.append("fichier", file);
  const response = await apiClient.post<VroomImportResult>(
    `/simulators/versions/${versionId}/import-vroom`,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return response.data;
}

// ============== Simulation Events (Admin) ==============

interface ListAllSimulationEventsParams {
  page?: number;
  page_size?: number;
  simulator_id?: number;
  user_id?: number;
  success?: boolean;
  is_save?: boolean;
  date_from?: string;
  date_to?: string;
}

export async function listAllSimulationEvents(
  params: ListAllSimulationEventsParams = {}
): Promise<SimulationEventAdminPaginated> {
  const response = await apiClient.get<SimulationEventAdminPaginated>(
    "/simulators/admin/simulation-events",
    { params }
  );
  return response.data;
}

// ============== User Simulation Events ==============

interface ListMySimulationEventsParams {
  page?: number;
  page_size?: number;
  simulator_id?: number;
  success?: boolean;
  is_save?: boolean;
  date_from?: string;
  date_to?: string;
  search?: string;
}

export async function listMySimulationEvents(
  params: ListMySimulationEventsParams = {}
): Promise<SimulationEventUserPaginated> {
  const response = await apiClient.get<SimulationEventUserPaginated>(
    "/simulators/simulation-events/my",
    { params }
  );
  return response.data;
}

export async function getSimulationEvent(
  eventId: number
): Promise<SimulationEventUser> {
  const response = await apiClient.get<SimulationEventUser>(
    `/simulators/simulation-events/${eventId}`
  );
  return response.data;
}

interface SaveSimulationEventPayload {
  inputs: Record<string, unknown>;
  outputs: Record<string, unknown>;
  success: boolean;
  errors?: string[];
  warnings?: string[];
  execution_time_ms?: number;
}

export async function saveSimulationEvent(
  versionId: number,
  data: SaveSimulationEventPayload
): Promise<SimulationEventUser> {
  const response = await apiClient.post<SimulationEventUser>(
    `/simulators/versions/${versionId}/simulation-events`,
    data
  );
  return response.data;
}

export async function deleteSimulationEvent(
  eventId: number
): Promise<MessageResponse> {
  const response = await apiClient.delete<MessageResponse>(
    `/simulators/simulation-events/${eventId}`
  );
  return response.data;
}

